import axios, {
	type AxiosError,
	type AxiosRequestConfig,
	type AxiosResponse,
	type InternalAxiosRequestConfig
} from "axios";

/**
 * Songbird fallback axios interceptor.
 *
 * Installs a single response interceptor on the default `axios` instance that
 * transparently retries against `fallbackBase` whenever a request to
 * `primaryBase` fails with a CORS/network error or HTTP 502.
 *
 * The same logic is used both in the browser (via `plugins/songbird-fallback.client.ts`)
 * and on the Nitro server (via `server/plugins/songbird-fallback.ts`) so that
 * direct page loads (SSR) and SPA navigations both benefit from the fallback.
 *
 * Notes:
 *  - Only requests whose final URL starts with `primaryBase` are eligible.
 *  - Each request is retried at most once (tracked via a flag on the config).
 *  - We intercept BOTH the success and error response handlers because
 *    callers (e.g. `pages/quote/[...stock].vue`) use
 *    `validateStatus: () => true`, which makes axios treat 502s as successful
 *    responses. Without the success-path branch, the error handler would
 *    never see them.
 *  - Returns a stable interceptor id so callers can eject it if needed.
 */

const RETRY_FLAG = "__songbirdFallbackRetried";

const NETWORK_FAILURE_CODES = new Set([
	"ECONNABORTED",
	"ECONNREFUSED",
	"ECONNRESET",
	"ENOTFOUND",
	"ETIMEDOUT",
	"EAI_AGAIN",
	"ERR_NETWORK"
]);

function isNetworkOrCorsError(error: AxiosError): boolean {
	if (error.response) return false;
	if (error.code && NETWORK_FAILURE_CODES.has(error.code)) return true;
	if (error.message === "Network Error") return true;
	return false;
}

function is502(error: AxiosError): boolean {
	return error.response?.status === 502;
}

function startsWithBase(url: string | undefined, base: string): boolean {
	if (!url || !base) return false;
	try {
		return url.startsWith(base);
	} catch {
		return false;
	}
}

export interface SongbirdFallbackOptions {
	primaryBase: string;
	fallbackBase: string;
	context?: string;
}

/**
 * Install the songbird → fallback retry interceptor on the default axios instance.
 *
 * Returns the interceptor id (number), or `null` if the interceptor wasn't
 * installed (e.g. one of the URLs is missing, both URLs are equal, or it was
 * already installed for this primary base).
 */
export function installSongbirdFallbackInterceptor(
	options: SongbirdFallbackOptions
): number | null {
	const { primaryBase, fallbackBase, context = "songbird" } = options;

	if (!primaryBase || !fallbackBase) return null;
	if (primaryBase === fallbackBase) return null;

	const flagKey = `__songbirdFallbackInstalled__${primaryBase}__${fallbackBase}`;
	const axiosWithFlag = axios as unknown as Record<string, unknown>;
	if (axiosWithFlag[flagKey]) {
		return null;
	}
	axiosWithFlag[flagKey] = true;

	console.info(
		`[${context}] Installed songbird fallback interceptor`,
		{ from: primaryBase, to: fallbackBase }
	);

	const buildRetryConfig = (
		original: InternalAxiosRequestConfig & { [RETRY_FLAG]?: boolean },
		fullUrl: string
	): AxiosRequestConfig & { [RETRY_FLAG]?: boolean } => {
		const retriedUrl = fullUrl.replace(primaryBase, fallbackBase);
		return {
			...original,
			url: retriedUrl,
			baseURL: undefined,
			[RETRY_FLAG]: true
		};
	};

	const resolveFullUrl = (
		original: InternalAxiosRequestConfig | undefined
	): string | undefined => {
		if (!original?.url) return undefined;
		if (/^https?:\/\//i.test(original.url)) return original.url;
		return (original.baseURL || "") + original.url;
	};

	return axios.interceptors.response.use(
		// SUCCESS path: callers use `validateStatus: () => true`, so 502s arrive
		// here (not in the error handler). We need to retry those against the
		// fallback ourselves.
		async (response: AxiosResponse) => {
			const original = response.config as
				| (InternalAxiosRequestConfig & { [RETRY_FLAG]?: boolean })
				| undefined;

			if (!original || original[RETRY_FLAG]) return response;
			if (response.status !== 502) return response;

			const fullUrl = resolveFullUrl(original);
			if (!startsWithBase(fullUrl, primaryBase)) return response;

			console.warn(
				`[${context}] Primary returned 502, retrying against fallback`,
				{ from: fullUrl, to: (fullUrl as string).replace(primaryBase, fallbackBase) }
			);

			try {
				return await axios.request(
					buildRetryConfig(original, fullUrl as string)
				);
			} catch (retryError) {
				console.warn(
					`[${context}] Fallback retry also failed; returning original 502 response`,
					{ error: (retryError as Error)?.message }
				);
				return response;
			}
		},
		// ERROR path: timeouts (ECONNABORTED), DNS failures, connection refused,
		// CORS errors in the browser, and any 5xx not allowed by validateStatus.
		async (error: AxiosError) => {
			const original = error.config as
				| (InternalAxiosRequestConfig & { [RETRY_FLAG]?: boolean })
				| undefined;

			if (!original) return Promise.reject(error);
			if (original[RETRY_FLAG]) return Promise.reject(error);

			const fullUrl = resolveFullUrl(original);

			if (!startsWithBase(fullUrl, primaryBase)) {
				return Promise.reject(error);
			}

			if (!isNetworkOrCorsError(error) && !is502(error)) {
				return Promise.reject(error);
			}

			console.warn(
				`[${context}] Request failed (` +
					(error.code || error.message || error.response?.status) +
					"), retrying against fallback",
				{ from: fullUrl, to: (fullUrl as string).replace(primaryBase, fallbackBase) }
			);

			return axios.request(buildRetryConfig(original, fullUrl as string));
		}
	);
}
